import React, { useEffect, useState, useCallback } from 'react';
import { Row, Col, Select, Typography, Space, Alert, Tag } from 'antd';
import { CalendarOutlined } from '@ant-design/icons';
import { notify } from 'components';
import { useNavigate } from 'react-router-dom';
import {
  fetchDashboardData,
  fetchSalesAnalytics,
  fetchPipelineAnalytics,
  fetchLeadConversionAnalytics,
  fetchExpenseAnalytics,
  fetchRecentDeals,
  fetchRecentLeads,
  fetchRecentInvoices
} from 'store/actions/AuthActions';
import useUrlFilters from 'hooks/useUrlFilters';

import KPIMetricsRow from './components/KPIMetricsRow';
import RevenueOverview from './components/RevenueOverview';
import PipelineOverview from './components/PipelineOverview';
import LeadFunnel from './components/LeadFunnel';
import InvoiceStatusCard from './components/InvoiceStatusCard';
import RecentActivities from './components/RecentActivities';

const { Title } = Typography;
const { Option } = Select;

const PERIOD_OPTIONS = [
  { value: 'all_time', label: 'All' },
  { value: 'today', label: 'Today' },
  { value: 'this_week', label: 'This Week' },
  { value: 'this_month', label: 'This Month' },
  { value: 'last_month', label: 'Last Month' },
  { value: 'this_quarter', label: 'This Quarter' },
  { value: 'last_quarter', label: 'Last Quarter' },
  { value: 'this_year', label: 'This Year' },
  { value: 'last_year', label: 'Last Year' },
  { value: 'this_fy', label: 'This FY' },
  { value: 'last_fy', label: 'Last FY' },
  { value: 'last_30_days', label: 'Last 30 Days' },
  { value: 'last_90_days', label: 'Last 90 Days' },
];

// Period lives in the URL so the dashboard view can be shared as a link.
const URL_FILTERS = {
  period: { type: 'string', default: 'this_month' },
};

const Dashboard = () => {
  const navigate = useNavigate();
  const [filters, setFilters] = useUrlFilters(URL_FILTERS);
  const period = filters.period;
  const setPeriod = (value) => setFilters({ period: value });
  const [, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Data states
  const [dashboardData, setDashboardData] = useState(null);
  const [salesData, setSalesData] = useState(null);
  const [pipelineData, setPipelineData] = useState(null);
  const [leadData, setLeadData] = useState(null);
  const [expenseData, setExpenseData] = useState(null);
  const [recentDeals, setRecentDeals] = useState([]);
  const [recentLeads, setRecentLeads] = useState([]);
  const [recentInvoices, setRecentInvoices] = useState([]);

  // Loading states for each section
  const [loadingKPI, setLoadingKPI] = useState(true);
  const [loadingSales, setLoadingSales] = useState(true);
  const [loadingPipeline, setLoadingPipeline] = useState(true);
  const [loadingLeads, setLoadingLeads] = useState(true);
  const [loadingExpenses, setLoadingExpenses] = useState(true);
  const [loadingRecent, setLoadingRecent] = useState(true);

  const handleNavigate = (path) => {
    navigate(path);
  };

  // Fetch dashboard counts (KPI data)
  const fetchKPIData = useCallback(async () => {
    setLoadingKPI(true);
    try {
      const response = await fetchDashboardData();
      console.log('Dashboard API Response:', response);
      setDashboardData(response);
    } catch (err) {
      console.error('Error fetching KPI data:', err);
    } finally {
      setLoadingKPI(false);
    }
  }, []);

  // Fetch sales analytics
  const fetchSales = useCallback(async (filterPreset) => {
    setLoadingSales(true);
    try {
      const response = await fetchSalesAnalytics(filterPreset);
      console.log('Sales Analytics Response:', response);
      setSalesData(response);
    } catch (err) {
      console.error('Error fetching sales data:', err);
    } finally {
      setLoadingSales(false);
    }
  }, []);

  // Fetch pipeline analytics
  const fetchPipeline = useCallback(async (filterPreset) => {
    setLoadingPipeline(true);
    try {
      const response = await fetchPipelineAnalytics(filterPreset);
      setPipelineData(response);
    } catch (err) {
      console.error('Error fetching pipeline data:', err);
    } finally {
      setLoadingPipeline(false);
    }
  }, []);

  // Fetch lead conversion analytics
  const fetchLeadConversion = useCallback(async (filterPreset) => {
    setLoadingLeads(true);
    try {
      const response = await fetchLeadConversionAnalytics(filterPreset);
      setLeadData(response);
    } catch (err) {
      console.error('Error fetching lead data:', err);
    } finally {
      setLoadingLeads(false);
    }
  }, []);

  // Fetch expense analytics
  const fetchExpenses = useCallback(async (filterPreset) => {
    setLoadingExpenses(true);
    try {
      const response = await fetchExpenseAnalytics(filterPreset);
      console.log('Expense Analytics Response:', response);
      setExpenseData(response);
    } catch (err) {
      console.error('Error fetching expense data:', err);
    } finally {
      setLoadingExpenses(false);
    }
  }, []);

  // Fetch recent activities
  const fetchRecentActivities = useCallback(async () => {
    setLoadingRecent(true);
    try {
      const [deals, leads, invoices] = await Promise.all([
        fetchRecentDeals(5).catch(() => []),
        fetchRecentLeads(5).catch(() => []),
        fetchRecentInvoices(5).catch(() => [])
      ]);
      setRecentDeals(deals);
      setRecentLeads(leads);
      setRecentInvoices(invoices);
    } catch (err) {
      console.error('Error fetching recent activities:', err);
    } finally {
      setLoadingRecent(false);
    }
  }, []);

  // Fetch all analytics data based on period
  const fetchAnalyticsData = useCallback(async (filterPreset) => {
    setLoading(true);
    setError(null);

    try {
      await Promise.all([
        fetchSales(filterPreset),
        fetchPipeline(filterPreset),
        fetchLeadConversion(filterPreset),
        fetchExpenses(filterPreset)
      ]);
    } catch (err) {
      setError('Some analytics data could not be loaded');
      notify('error', 'Error loading some dashboard data');
    } finally {
      setLoading(false);
    }
  }, [fetchSales, fetchPipeline, fetchLeadConversion, fetchExpenses]);

  // Initial load
  useEffect(() => {
    fetchKPIData();
    fetchRecentActivities();
  }, []);

  // Fetch analytics whenever the period changes (incl. initial load and back/forward)
  useEffect(() => {
    fetchAnalyticsData(period);
  }, [period, fetchAnalyticsData]);

  // Handle period change
  const handlePeriodChange = (newPeriod) => {
    setPeriod(newPeriod);
  };

  // Combine dashboard data with sales data for KPI metrics
  // Use period-filtered data where available
  const getUniqueInvoiceCount = () => {
    const invoices = salesData?.invoices || [];
    const uniqueIds = new Set(invoices.map(inv => inv.invoice_id || inv.id));
    return uniqueIds.size;
  };

  const kpiData = {
    ...dashboardData,
    revenue: {
      total: salesData?.summary?.total_revenue || 0,
      growth: salesData?.summary?.revenue_growth
    },
    // Period-filtered expenses from expense analytics
    expenses_total: expenseData?.summary?.total_expenses || 0,
    expenses_growth: null, // Could be calculated if needed
    // Period-filtered invoice count from sales data
    invoices_total: getUniqueInvoiceCount() || dashboardData?.invoices_total || 0,
    pipeline: {
      total: pipelineData?.summary?.total_value || 0,
      growth: pipelineData?.summary?.pipeline_growth
    }
  };

  // Derive invoice status from sales data invoices
  const deriveInvoiceStatus = () => {
    const invoices = salesData?.invoices || [];
    const statusCounts = {};
    const statusAmounts = {};

    invoices.forEach(inv => {
      const status = inv.status || 'DRAFT';
      statusCounts[status] = (statusCounts[status] || 0) + 1;
      statusAmounts[status] = (statusAmounts[status] || 0) + (inv.total_with_tax || inv.line_total || 0);
    });

    // Count unique invoices by status
    const uniqueInvoices = {};
    invoices.forEach(inv => {
      const key = inv.invoice_id || inv.id;
      if (!uniqueInvoices[key]) {
        uniqueInvoices[key] = {
          status: inv.status || 'DRAFT',
          amount: inv.total_with_tax || inv.line_total || 0
        };
      }
    });

    const uniqueStatusCounts = {};
    const uniqueStatusAmounts = {};
    Object.values(uniqueInvoices).forEach(inv => {
      uniqueStatusCounts[inv.status] = (uniqueStatusCounts[inv.status] || 0) + 1;
      uniqueStatusAmounts[inv.status] = (uniqueStatusAmounts[inv.status] || 0) + inv.amount;
    });

    return {
      by_status: ['PAID', 'SENT', 'PARTIAL', 'DRAFT', 'CANCELLED']
        .map(status => ({
          status,
          count: uniqueStatusCounts[status] || 0,
          amount: uniqueStatusAmounts[status] || 0
        }))
        .filter(item => item.count > 0)
    };
  };

  const invoiceStatus = deriveInvoiceStatus();

  return (
    <div style={{
      height: 'calc(100vh - 64px)',
      overflow: 'auto',
      backgroundColor: '#f0f2f5'
    }}
      className="dashboard-page"
    >
      {/* Header with Period Selector */}
      <div className="page-header-bar" style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        flexWrap: 'wrap',
        gap: '12px',
        marginBottom: '16px'
      }}>
        <Title level={3} style={{ margin: 0 }}>Business Dashboard</Title>
        <Space size="middle" align="center">
          {salesData?.summary?.date_range && (
            <Tag
              icon={<CalendarOutlined />}
              color="blue"
              style={{
                fontSize: '13px',
                padding: '4px 12px',
                margin: 0,
                fontWeight: 500
              }}
            >
              {salesData.summary.date_range}
            </Tag>
          )}
          <Space>
            <span style={{ fontSize: '13px', color: '#595959' }}>Period:</span>
            <Select
              value={period}
              onChange={handlePeriodChange}
              style={{ width: 150 }}
              size="small"
            >
              {PERIOD_OPTIONS.map(opt => (
                <Option key={opt.value} value={opt.value}>{opt.label}</Option>
              ))}
            </Select>
          </Space>
        </Space>
      </div>

      {/* Error Alert */}
      {error && (
        <Alert
          message={error}
          type="warning"
          closable
          onClose={() => setError(null)}
          style={{ marginBottom: '16px' }}
        />
      )}

      {/* KPI Metrics Row */}
      <KPIMetricsRow
        data={kpiData}
        loading={loadingKPI || loadingSales || loadingExpenses}
        onNavigate={handleNavigate}
      />

      {/* Main Charts Row */}
      <Row gutter={[16, 16]} style={{ marginBottom: '16px' }}>
        {/* Revenue Overview */}
        <Col xs={24} lg={14}>
          <RevenueOverview
            data={salesData}
            loading={loadingSales}
          />
        </Col>

        {/* Sales Pipeline */}
        <Col xs={24} lg={10}>
          <PipelineOverview
            data={pipelineData}
            loading={loadingPipeline}
          />
        </Col>
      </Row>

      {/* Secondary Charts Row */}
      <Row gutter={[16, 16]} style={{ marginBottom: '16px' }}>
        {/* Lead Conversion */}
        <Col xs={24} md={12} lg={10}>
          <LeadFunnel
            data={leadData}
            loading={loadingLeads}
          />
        </Col>

        {/* Invoice Status */}
        <Col xs={24} md={12} lg={6}>
          <InvoiceStatusCard
            data={invoiceStatus}
            loading={loadingSales}
          />
        </Col>

        {/* Recent Activities */}
        <Col xs={24} lg={8}>
          <RecentActivities
            deals={recentDeals}
            leads={recentLeads}
            invoices={recentInvoices}
            loading={loadingRecent}
            onNavigate={handleNavigate}
          />
        </Col>
      </Row>
    </div>
  );
};

export default Dashboard;
